import { useEffect, useRef } from 'react'
import { useReactFlow, useStore } from '@xyflow/react'
import { JourneyNode } from './types/journey-nodes'

type ViewportAdjusterProps = {
    nodes: JourneyNode[]
}

/**
 * Sets initial React Flow viewport to position content at the top with horizontal centering.
 * 
 * Instead of using React Flow's fitView (which centers vertically), this component
 * calculates the viewport to center horizontally while positioning content at the top.
 * 
 * Must be rendered inside ReactFlowProvider.
 */
export const ViewportAdjuster = ({ nodes }: ViewportAdjusterProps) => {
    const { setViewport } = useReactFlow()
    const hasSet = useRef(false)
    
    const { width: containerWidth, height: containerHeight } = useStore((state) => ({
        width: state.width,
        height: state.height,
    }))
    
    useEffect(() => {
        // Only set viewport once when nodes are loaded and container is measured
        if (hasSet.current || !nodes.length || containerWidth === 0 || containerHeight === 0) {
            return
        }
        
        // Calculate bounds of all nodes
        let minX = Infinity
        let maxX = -Infinity
        
        nodes.forEach(node => {
            const x = node.position.x
            const width = node.measured?.width ?? 250
            minX = Math.min(minX, x)
            maxX = Math.max(maxX, x + width)
        })
        
        // Content width
        const contentWidth = maxX - minX
        
        // Calculate X to center horizontally
        // Center of container - center of content
        const centerX = (containerWidth / 2) - (contentWidth / 2) - minX
        
        // Position at top with padding
        const topPadding = 40
        
        // Set viewport without animation (duration: 0)
        setViewport({
            x: centerX,
            y: topPadding,
            zoom: 1,
        }, { duration: 0 })
        
        hasSet.current = true
    }, [nodes, containerWidth, containerHeight, setViewport])
    
    return null
}
